import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Empty, Input, Segmented, Space, Tag, Switch, Tooltip, Typography } from 'antd';
import { ClearOutlined } from '@ant-design/icons';
import type { NocStreamDirection } from '@goelectric/shared-types';
import type { NocStreamMode, NocStreamRecord } from '@/features/noc/hooks/useNocStream';

type FeedFilter = 'all' | 'station' | 'sim' | 'alert';

const UPLINK_TYPES = new Set([
  'station.updated',
  'connector.updated',
  'meter.tick',
  'session.started',
  'session.ended',
]);

const COMMAND_TYPES = new Set(['command.sent', 'command.result']);

function matchesFilter(type: string, filter: FeedFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'station') return UPLINK_TYPES.has(type);
  if (filter === 'sim') {
    return type === 'sim.log' || type === 'sim.progress' || COMMAND_TYPES.has(type);
  }
  return type === 'alert.created' || type === 'alert.updated';
}

function directionFromPayload(data: unknown): NocStreamDirection | null {
  if (typeof data === 'object' && data !== null) {
    const direction = (data as { direction?: unknown }).direction;
    if (direction === 'cs->csms' || direction === 'csms->cs') {
      return direction;
    }
  }
  return null;
}

/** Dirección mostrada: la del payload (backend) o la inferida por tipo de evento. */
function directionOf(record: NocStreamRecord): { label: string; color: string } {
  const payloadDirection = directionFromPayload(record.data);
  if (payloadDirection === 'cs->csms') {
    return { label: 'cs → csms', color: 'blue' };
  }
  if (payloadDirection === 'csms->cs') {
    return { label: 'csms → cs', color: 'gold' };
  }
  if (UPLINK_TYPES.has(record.type)) {
    return { label: 'cs → csms', color: 'blue' };
  }
  if (record.type.startsWith('sim.')) {
    return { label: 'csms (sim)', color: 'default' };
  }
  return { label: 'csms', color: 'default' };
}

function fieldOf(data: unknown, key: string): string | null {
  if (typeof data === 'object' && data !== null) {
    const value = (data as Record<string, unknown>)[key];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }
  return null;
}

function shortId(value: string): string {
  return value.length > 10 ? `${value.slice(0, 8)}…` : value;
}

interface SimFeedProps {
  events: NocStreamRecord[];
  mode: NocStreamMode;
  onClear: () => void;
}

const MODE_BADGE: Record<NocStreamMode, { status: 'processing' | 'success' | 'default'; label: string }> = {
  connecting: { status: 'processing', label: 'Conectando SSE' },
  open: { status: 'success', label: 'SSE conectado' },
  unavailable: { status: 'default', label: 'SSE no disponible' },
};

/** Feed de eventos SSE en vivo, presentado como traza JSON-RPC (cs ↔ csms). */
export function SimFeed({ events, mode, onClear }: SimFeedProps) {
  const [filter, setFilter] = useState<FeedFilter>('all');
  const [query, setQuery] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return events
      .filter((record) => matchesFilter(record.type, filter))
      .filter((record) => {
        if (!term) return true;
        const haystack = `${record.type} ${JSON.stringify(record.data)}`.toLowerCase();
        return haystack.includes(term);
      })
      .slice(-80);
  }, [events, filter, query]);

  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [filtered.length, autoScroll]);

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
        <Space wrap size="small">
          <Badge status={MODE_BADGE[mode].status} text={MODE_BADGE[mode].label} />
          <Tooltip title="El backend añade direction/action/correlationId a los eventos OCPP. El requestId identifica cada comando downlink (command.sent/result).">
            <Tag>traza: requestId / correlationId</Tag>
          </Tooltip>
        </Space>
        <Space size="small">
          <Switch
            checked={autoScroll}
            onChange={setAutoScroll}
            size="small"
            aria-label="Auto-scroll del feed"
          />{' '}
          <Typography.Text type="secondary">auto-scroll</Typography.Text>
          <Button size="small" icon={<ClearOutlined />} onClick={onClear}>
            Limpiar
          </Button>
        </Space>
      </Space>

      <Space wrap>
        <Segmented<FeedFilter>
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: 'Todo' },
            { value: 'station', label: 'Estación' },
            { value: 'sim', label: 'Simulación' },
            { value: 'alert', label: 'Alertas' },
          ]}
        />
        <Input.Search
          allowClear
          placeholder="Filtrar por texto o stationId"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          style={{ width: 240 }}
        />
      </Space>

      <div className="lab-feed" ref={containerRef} role="log" aria-live="polite">
        {filtered.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              events.length === 0
                ? 'Esperando eventos del stream SSE…'
                : 'Sin eventos para el filtro'
            }
          />
        ) : (
          <ul className="lab-feed__list">
            {filtered.map((record) => {
              const direction = directionOf(record);
              const stationId = fieldOf(record.data, 'stationId');
              const action = fieldOf(record.data, 'action');
              const requestId = fieldOf(record.data, 'requestId');
              const correlationId = fieldOf(record.data, 'correlationId');
              const isCommandResult = record.type === 'command.result';
              const time = new Date(record.at).toLocaleTimeString('es-CO', { hour12: false });
              return (
                <li key={record.seq} className="lab-feed__item">
                  <details>
                    <summary>
                      <Tag color={direction.color}>{direction.label}</Tag>
                      <strong>{record.type}</strong>
                      {action && <Tag color="geekblue">{action}</Tag>}
                      {stationId && <Tag>{stationId}</Tag>}
                      {requestId && (
                        <Tooltip title={`requestId: ${requestId}`}>
                          <Typography.Text code style={{ fontSize: 11 }}>
                            req:{shortId(requestId)}
                          </Typography.Text>
                        </Tooltip>
                      )}
                      {correlationId ? (
                        <Tooltip title={`correlationId (OCPP): ${correlationId}`}>
                          <Typography.Text code style={{ fontSize: 11 }}>
                            corr:{shortId(correlationId)}
                          </Typography.Text>
                        </Tooltip>
                      ) : (
                        isCommandResult && (
                          <Tooltip title="El core no informó correlationId OCPP para esta petición.">
                            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                              corr: n/a
                            </Typography.Text>
                          </Tooltip>
                        )
                      )}
                      <span className="lab-feed__time">{time}</span>
                    </summary>
                    <pre className="lab-json">{JSON.stringify(record.data, null, 2)}</pre>
                  </details>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Space>
  );
}
